import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, Settings, LogOut, Bell, LifeBuoy, Send, X, Sun, Moon, Search } from "lucide-react";
import { useTheme } from "next-themes";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import { isPanelRoutePath, toPanelPath, withPanelBase } from "@/lib/authz";
const DISMISSED_SYSTEM_KEY_PREFIX = "obihub_dismissed_system_notifications";
const ORDER_NOTIFICATIONS_KEY_PREFIX = "obihub_order_notifications";
const ORDER_COUNT_KEY_PREFIX = "obihub_new_order_count";
const DISMISSED_ORDER_KEY_PREFIX = "obihub_dismissed_order_notifications";
const ACTIVE_USER_ID_KEY = "obihub_active_user_id";
const SUPPORT_TOPICS = [
  { key: "entegrasyon", label: "Entegrasyon" },
  { key: "fatura", label: "Fatura / Muhasebe" },
  { key: "siparis", label: "Sipariş / Operasyon" },
  { key: "teknik", label: "Teknik Sorun" },
  { key: "oneris", label: "Öneri / Geliştirme" },
];

function breadcrumbFromPath(pathname, segmentReplacements = {}) {
  const panelPath = toPanelPath(pathname);
  if (panelPath === "/panel") return "Panel";
  const segments = panelPath.replace("/panel/", "").split("/");
  const map = {
    "urunler": "\u00dcr\u00fcnler",
    "entegrasyonlar": "Entegrasyonlar",
    "siparisler": "Sipari\u015fler",
    "urun-stok": "\u00dcr\u00fcn Stok",
    "urun-fiyat": "\u00dcr\u00fcn Fiyat",
    "stok": "Stok & Depolar",
    "raporlar": "Raporlar",
    "musteriler": "M\u00fc\u015fteriler",
    "ayarlar": "Ayarlar",
    "hesabim": "Hesabım",
    "firmalarim": "Firmalarım",
    "abonelik": "Abonelik",
    "destek-taleplerim": "Destek Taleplerim",
    "davet-et-kazan": "Davet Et, Kazan",
    "uygulamalar": "Uygulamalar",
    "loglar": "Loglar",
    "satislar": "Sat\u0131\u015flar",
    "faturalar": "Faturalar",
    "giderler": "Giderler",
    "gelen-e-faturalar": "Gelen E-Faturalar",
    "operasyon": "Operasyon",
    "hepsijet-etiket": "HepsiJet",
  };
  const isLikelyUuid = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(s || ""));
  const rendered = segments.map((s, idx) => {
    if (segmentReplacements[s]) return segmentReplacements[s];
    if (map[s]) return map[s];
    if (isLikelyUuid(s)) {
      const prev = String(segments[idx - 1] || "");
      if (prev === "urunler") return "Ürün Detayı";
      if (prev === "faturalar") return "Fatura Detayı";
      return "Detay";
    }
    return s;
  });
  return "Panel / " + rendered.join(" / ");
}

function notificationKey(item) {
  return `${item.integration_id}:${item.channel_order_id}:${item.status}`;
}

function resolveChannelLabel(notification) {
  const explicit = String(notification?.integration_name || notification?.channel || "").trim().toLowerCase();
  const byName = {
    trendyol: "Trendyol",
    hepsiburada: "Hepsiburada",
    woocommerce: "WooCommerce",
    hepsijet: "HepsiJet",
    amazon: "Amazon",
    n11: "N11",
    parasut: "Paraşüt",
  };
  if (explicit && byName[explicit]) return byName[explicit];
  const orderId = String(notification?.channel_order_id || "").trim().toUpperCase();
  if (orderId.startsWith("TY-")) return "Trendyol";
  if (orderId.startsWith("HB-")) return "Hepsiburada";
  if (orderId.startsWith("WC-")) return "WooCommerce";
  if (orderId.startsWith("AZ-")) return "Amazon";
  return "Entegrasyon";
}

function normalizeNotifications(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    if (!item) continue;
    const key = item.id_key || notificationKey(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({ ...item, id_key: key });
  }
  return out;
}

function playMoneySound() {
  try {
    const audio = new Audio("/sounds/cha_ching.wav");
    audio.volume = 0.9;
    audio.currentTime = 0;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        // ignore autoplay errors
      });
    }
  } catch {
    // ignore sound failures
  }
}

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const [accountOpen, setAccountOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [supportNotifications, setSupportNotifications] = useState([]);
  const [integrationNameById, setIntegrationNameById] = useState({});
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportModuleEnabled, setSupportModuleEnabled] = useState(true);
  const [supportSaving, setSupportSaving] = useState(false);
  const [supportStep, setSupportStep] = useState(1);
  const [supportTopic, setSupportTopic] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const supportNotifMarkerRef = useRef("");
  const supportNotifInitializedRef = useRef(false);
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);
  const notifPanelRef = useRef(null);
  const supportRef = useRef(null);
  const supportPanelRef = useRef(null);
  const pollingRef = useRef(false);
  const activeIntegrationIdsRef = useRef(new Set());

  const breadcrumb = breadcrumbFromPath(location.pathname, integrationNameById);
  const tenantName = user?.company_name || "Firma";
  const userEmail = user?.email || "";
  const allNotifications = [...supportNotifications, ...systemNotifications, ...notifications];
  const storageKeys = useMemo(() => {
    const userScope = String(user?.id || localStorage.getItem(ACTIVE_USER_ID_KEY) || "anon").trim() || "anon";
    return {
      dismissedSystem: `${DISMISSED_SYSTEM_KEY_PREFIX}:${userScope}`,
      orderNotifications: `${ORDER_NOTIFICATIONS_KEY_PREFIX}:${userScope}`,
      orderCount: `${ORDER_COUNT_KEY_PREFIX}:${userScope}`,
      dismissedOrders: `${DISMISSED_ORDER_KEY_PREFIX}:${userScope}`,
    };
  }, [user?.id]);

  const getDismissedSystemIds = useCallback(() => {
    try {
      const arr = JSON.parse(localStorage.getItem(storageKeys.dismissedSystem) || "[]");
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  }, [storageKeys.dismissedSystem]);

  const dismissSystemNotification = (idKey) => {
    if (!idKey) return;
    const set = getDismissedSystemIds();
    set.add(idKey);
    localStorage.setItem(storageKeys.dismissedSystem, JSON.stringify(Array.from(set)));
    setSystemNotifications((prev) => prev.filter((x) => x.id_key !== idKey));
  };

  const getDismissedOrderKeys = useCallback(() => {
    try {
      const arr = JSON.parse(localStorage.getItem(storageKeys.dismissedOrders) || "[]");
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  }, [storageKeys.dismissedOrders]);

  const rememberDismissedOrderKeys = useCallback((keys) => {
    const keyList = Array.isArray(keys) ? keys : [keys];
    const next = getDismissedOrderKeys();
    for (const key of keyList) {
      const value = String(key || "").trim();
      if (value) next.add(value);
    }
    const compact = Array.from(next).slice(-400);
    localStorage.setItem(storageKeys.dismissedOrders, JSON.stringify(compact));
  }, [getDismissedOrderKeys, storageKeys.dismissedOrders]);

  const pruneStaleNotifications = useCallback((rawList) => {
    const list = normalizeNotifications(rawList);
    const dismissed = getDismissedOrderKeys();
    const activeIds = activeIntegrationIdsRef.current;
    return list.filter((item) => {
      const key = item.id_key || notificationKey(item);
      if (key && dismissed.has(key)) return false;
      if (!item?.integration_id) return true;
      if (!(activeIds instanceof Set) || activeIds.size === 0) return true;
      return activeIds.has(String(item.integration_id));
    });
  }, [getDismissedOrderKeys]);

  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) {
          setNotifOpen(false);
        }
      }
      if (
        supportRef.current &&
        !supportRef.current.contains(e.target) &&
        supportPanelRef.current &&
        !supportPanelRef.current.contains(e.target)
      ) {
        setSupportOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!isPanelRoutePath(location.pathname)) return;
    (async () => {
      try {
        const [integrationsRes, trialRes] = await Promise.all([
          api.get("/integrations").catch(() => []),
          api.get("/billing/trial-status").catch(() => null),
        ]);
        if (!mounted) return;
        const ints = Array.isArray(integrationsRes) ? integrationsRes : [];
        activeIntegrationIdsRef.current = new Set(
          ints
            .map((x) => String(x?.id || "").trim())
            .filter(Boolean)
        );
        const persistedRaw = (() => {
          try {
            return JSON.parse(localStorage.getItem(storageKeys.orderNotifications) || "[]");
          } catch {
            return [];
          }
        })();
        const persisted = pruneStaleNotifications(persistedRaw);
        setNotifications(persisted);
        setNewOrderCount(persisted.length);
        localStorage.setItem(storageKeys.orderNotifications, JSON.stringify(persisted));
        localStorage.setItem(storageKeys.orderCount, String(persisted.length));
        const dismissed = getDismissedSystemIds();
        const items = [];
        if (ints.length === 0) {
          items.push({
            id_key: "sys-no-integration",
            kind: "system",
            title: "Aramıza Hoşgeldin",
            message: "15 gün ücretsiz deneme başladı. İlk entegrasyonunu ekleyerek kuruluma hemen başlayabilirsin.",
            route: "/panel/entegrasyonlar",
            created_at: new Date().toISOString(),
          });
        }
        if (trialRes?.is_trial) {
          const daysLeft = Number(trialRes?.days_left ?? 0);
          items.push({
            id_key: `sys-trial-reminder-${daysLeft}`,
            kind: "system",
            title: daysLeft <= 1 ? "Son gün: Ödeme yapmalısınız" : `15 Gün Ücretsiz Deneme • ${daysLeft} gün kaldı`,
            message:
              daysLeft <= 1
                ? "Deneme bugün bitiyor. Kesintisiz devam etmek için abonelik sayfasından ödemenizi tamamlayın."
                : daysLeft <= 7
                ? `Deneme sürenizde ${daysLeft} gün kaldı. Abonelik sayfasından ödeme planınızı hazırlayabilirsiniz.`
                : "Entegrasyonlarını kur, bağlantı ayarlarını tamamla ve ücretsiz deneme süresini verimli kullan.",
            route: "/panel/hesabim/abonelik",
            created_at: new Date().toISOString(),
          });
        }
        setSystemNotifications(items.filter((x) => !dismissed.has(x.id_key)));
      } catch {
        // ignore onboarding notification fetch errors
      }
    })();
    return () => {
      mounted = false;
    };
  }, [location.pathname, getDismissedSystemIds, pruneStaleNotifications, storageKeys.orderCount, storageKeys.orderNotifications]);

  useEffect(() => {
    let cancelled = false;
    if (!isPanelRoutePath(location.pathname)) return undefined;
    (async () => {
      try {
        let res;
        try {
          res = await api.get("/integration-providers");
        } catch (primaryErr) {
          res = await api.get("/integrations/providers");
        }
        if (cancelled) return;
        const rows = Array.isArray(res?.items) ? res.items : [];
        const enabled = rows.some((x) => String(x?.key || "").toLowerCase() === "support_center");
        setSupportModuleEnabled(enabled);
        if (!enabled) {
          setSupportOpen(false);
          setSupportNotifications([]);
        }
      } catch {
        if (!cancelled) setSupportModuleEnabled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;

    const applyIncomingNotifications = (incoming) => {
      if (!Array.isArray(incoming) || incoming.length === 0) return;
      const current = normalizeNotifications(
        (() => {
          try {
            return JSON.parse(localStorage.getItem(storageKeys.orderNotifications) || "[]");
          } catch {
            return [];
          }
        })()
      );
      const dismissed = getDismissedOrderKeys();

      const seen = new Set(current.map((n) => n.id_key || notificationKey(n)));
      const added = [];
      for (const x of incoming) {
        const k = x.id_key || notificationKey(x);
        if (!k || seen.has(k) || dismissed.has(k)) continue;
        if (x?.integration_id && activeIntegrationIdsRef.current.size > 0 && !activeIntegrationIdsRef.current.has(String(x.integration_id))) continue;
        seen.add(k);
        added.push({ ...x, id_key: k, created_at: x.created_at || new Date().toISOString() });
      }
      if (added.length === 0) return;

      const merged = pruneStaleNotifications([...added, ...current]).slice(0, 100);
      const nextCount = merged.length;
      localStorage.setItem(storageKeys.orderNotifications, JSON.stringify(merged));
      localStorage.setItem(storageKeys.orderCount, String(nextCount));
      if (mounted) {
        setNotifications(merged);
        setNewOrderCount(nextCount);
      }
      window.dispatchEvent(new CustomEvent("obihub:new-orders", { detail: { count: nextCount, notifications: merged } }));

      if (added.some((x) => x.status === "processing")) {
        playMoneySound();
      }
    };

    const applyCargoErrorNotifications = (rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return;
      const dismissed = getDismissedSystemIds();
      const routeBase = withPanelBase("/panel/entegrasyonlar", user, location.pathname);
      const candidates = rows
        .filter((o) => String(o?.cargo_status || "").toLowerCase() === "error" && String(o?.cargo_error_message || "").trim())
        .map((o) => {
          const at = String(o?.cargo_error_at || o?.updated_at || o?.order_date || "").trim();
          return {
            kind: "system",
            id_key: `cargo-error:${o?.id || o?.channel_order_id || "order"}:${at}`,
            title: `Kargo etiketi oluşturulamadı (${o?.channel_order_id || "-"})`,
            message: String(o?.cargo_error_message || "").trim(),
            route: routeBase,
            created_at: at || new Date().toISOString(),
          };
        })
        .filter((x) => x.id_key && !dismissed.has(x.id_key));

      if (!candidates.length) return;

      setSystemNotifications((prev) => {
        const base = Array.isArray(prev) ? prev.filter((x) => !String(x?.id_key || "").startsWith("cargo-error:")) : [];
        const seen = new Set(base.map((x) => x.id_key));
        const merged = [...base];
        for (const item of candidates) {
          if (seen.has(item.id_key)) continue;
          seen.add(item.id_key);
          merged.push(item);
        }
        return merged;
      });
    };

    const pollProcessingOrders = async () => {
      if (pollingRef.current) return;
      if (!isPanelRoutePath(location.pathname)) return;
      pollingRef.current = true;
      try {
        // IMPORTANT: do not trigger sync-orders here; it causes heavy load on every page.
        // Poll only existing orders list and build notification candidates from DB state.
        const [processingRes, pendingRes] = await Promise.all([
          api.get("/orders?status=processing&page=1&per_page=30&sort_by=date_desc"),
          api.get("/orders?status=pending&page=1&per_page=30&sort_by=date_desc"),
        ]);
        const rows = [
          ...(Array.isArray(processingRes?.items) ? processingRes.items : []),
          ...(Array.isArray(pendingRes?.items) ? pendingRes.items : []),
        ];
        applyCargoErrorNotifications(rows);
        const collected = rows.map((o) => ({
          integration_id: o.integration_id,
          channel_order_id: o.channel_order_id,
          customer_name: o.customer_name || "Müşteri",
          total_amount: Number(o.total_amount || 0),
          currency: o.currency || "TRY",
          status: o.status || "processing",
          route: withPanelBase(`/panel/entegrasyonlar/${o.integration_id}/siparisler`, user, location.pathname),
        }));
        applyIncomingNotifications(collected);
      } catch {
        // subscription/authorization failures can happen; avoid unhandled promise rejection noise
      } finally {
        pollingRef.current = false;
      }
    };

    pollProcessingOrders();
    const timer = window.setInterval(pollProcessingOrders, 60000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [getDismissedOrderKeys, location.pathname, pruneStaleNotifications, storageKeys.orderCount, storageKeys.orderNotifications, user]);

  useEffect(() => {
    let cancelled = false;
    if (!isPanelRoutePath(location.pathname) || !supportModuleEnabled) return undefined;

    const pollSupportNotifications = async () => {
      try {
        const res = await api.get("/support/notifications", { params: { limit: 10 } });
        if (cancelled) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        const mapped = items.map((x) => ({
          kind: "support",
          id_key: `support:${x.ticket_id}:${x.last_super_message_at || ""}`,
          ticket_id: x.ticket_id,
          title: x.subject || "Destek Talebi",
          message: x.preview || "Destek ekibinden yeni yanıt geldi.",
          status: x.status || "in_progress",
          route: withPanelBase("/panel/hesabim/destek-taleplerim", user, location.pathname),
          created_at: x.last_super_message_at || new Date().toISOString(),
        }));
        setSupportNotifications(mapped);

        const marker = mapped[0]?.id_key || "";
        if (!supportNotifInitializedRef.current) {
          supportNotifInitializedRef.current = true;
          supportNotifMarkerRef.current = marker;
          return;
        }
        if (marker && marker !== supportNotifMarkerRef.current) {
          supportNotifMarkerRef.current = marker;
          toast.info("Destek ekibinden yeni yanıt geldi.");
        }
      } catch {
        // ignore support polling errors
      }
    };

    pollSupportNotifications();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, user, supportModuleEnabled]);

  useEffect(() => {
    const match = toPanelPath(location.pathname).match(/^\/panel\/entegrasyonlar\/([^/]+)/);
    const integrationId = match?.[1];
    if (!integrationId) return;
    if (integrationNameById[integrationId]) return;

    let active = true;
    (async () => {
      try {
        const data = await api.get(`/integrations/${integrationId}`);
        const name = data?.name || data?.store_name || "";
        if (!active || !name) return;
        setIntegrationNameById((prev) => ({ ...prev, [integrationId]: name }));
      } catch {
        // keep UUID segment when integration name cannot be resolved
      }
    })();

    return () => {
      active = false;
    };
  }, [location.pathname, integrationNameById]);

  useEffect(() => {
    const match = toPanelPath(location.pathname).match(/^\/panel\/urunler\/([^/]+)/);
    const productId = String(match?.[1] || "").trim();
    if (!productId) return;
    if (integrationNameById[productId]) return;

    let active = true;
    (async () => {
      try {
        const data = await api.get(`/products/${productId}`);
        const name = String(data?.name || "").trim();
        if (!active || !name) return;
        setIntegrationNameById((prev) => ({ ...prev, [productId]: name }));
      } catch {
        // keep generic breadcrumb fallback label
      }
    })();

    return () => {
      active = false;
    };
  }, [location.pathname, integrationNameById]);

  useEffect(() => {
    const savedCount = Number(localStorage.getItem(storageKeys.orderCount) || "0");
    const savedNotificationsRaw = (() => {
      try {
        return JSON.parse(localStorage.getItem(storageKeys.orderNotifications) || "[]");
      } catch {
        return [];
      }
    })();
    const savedNotifications = pruneStaleNotifications(savedNotificationsRaw);
    setNotifications(savedNotifications);
    setNewOrderCount(savedNotifications.length || savedCount);
    localStorage.setItem(storageKeys.orderNotifications, JSON.stringify(savedNotifications));
    localStorage.setItem(storageKeys.orderCount, String(savedNotifications.length || savedCount));

    const onNewOrders = (e) => {
      const count = Number(e?.detail?.count || 0);
      setNewOrderCount(count);
      if (Array.isArray(e?.detail?.notifications)) {
        const next = normalizeNotifications(e.detail.notifications);
        const pruned = pruneStaleNotifications(next);
        setNotifications(pruned);
        setNewOrderCount(pruned.length);
        localStorage.setItem(storageKeys.orderNotifications, JSON.stringify(pruned));
        localStorage.setItem(storageKeys.orderCount, String(pruned.length));
      } else {
        try {
          const latest = pruneStaleNotifications(JSON.parse(localStorage.getItem(storageKeys.orderNotifications) || "[]"));
          setNotifications(latest);
          setNewOrderCount(latest.length || count);
          localStorage.setItem(storageKeys.orderNotifications, JSON.stringify(latest));
          localStorage.setItem(storageKeys.orderCount, String(latest.length || count));
        } catch {
          setNotifications([]);
        }
      }
    };
    window.addEventListener("obihub:new-orders", onNewOrders);
    return () => window.removeEventListener("obihub:new-orders", onNewOrders);
  }, [pruneStaleNotifications, storageKeys.orderCount, storageKeys.orderNotifications]);

  const handleLogout = () => {
    setAccountOpen(false);
    logout();
    navigate("/giris");
  };

  const formatAmount = (amount, currency = "TRY") => {
    try {
      return new Intl.NumberFormat("tr-TR", { style: "currency", currency }).format(Number(amount || 0));
    } catch {
      return `${Number(amount || 0).toFixed(2)} ${currency}`;
    }
  };

  const openNotifications = () => {
    setNotifOpen((v) => !v);
  };

  const submitSupportMessage = async () => {
    if (!supportTopic || !supportMessage.trim()) return;
    setSupportSaving(true);
    try {
      const topicLabel = SUPPORT_TOPICS.find((x) => x.key === supportTopic)?.label || "Genel Destek";
      const res = await api.post("/support/tickets", {
        topic: supportTopic,
        subject: `${topicLabel} Talebi`,
        message: supportMessage.trim(),
        live_requested: false,
        source: "bot",
      });
      setSupportTopic("");
      setSupportStep(1);
      setSupportMessage("");
      setSupportOpen(false);
      window.dispatchEvent(new CustomEvent("obihub:new-support-message", { detail: { at: Date.now() } }));
      toast.success(res?.message || "Destek talebiniz alındı.");
    } catch (e) {
      toast.error(e?.message || "Destek talebi gönderilemedi.");
    } finally {
      setSupportSaving(false);
    }
  };

  const openOrderNotification = (item) => {
    if (item?.kind === "system") {
      dismissSystemNotification(item.id_key);
    } else if (item?.kind === "support") {
      setSupportNotifications((prev) => prev.filter((n) => n.id_key !== item.id_key));
    } else {
      const currentKey = item.id_key || notificationKey(item);
      rememberDismissedOrderKeys(currentKey);
      const nextList = notifications.filter((n) => (n.id_key || notificationKey(n)) !== currentKey);
      setNotifications(nextList);
      setNewOrderCount(nextList.length);
      localStorage.setItem(storageKeys.orderNotifications, JSON.stringify(nextList));
      localStorage.setItem(storageKeys.orderCount, String(nextList.length));
      window.dispatchEvent(new CustomEvent("obihub:new-orders", { detail: { count: nextList.length, notifications: nextList } }));
    }
    setNotifOpen(false);
    if (item?.route) {
      navigate(withPanelBase(item.route, user, location.pathname), {
        state: {
          fromNotification: true,
          refreshAt: Date.now(),
          ticketId: item.ticket_id || null,
        },
      });
      return;
    }
    if (item?.integration_id) {
      navigate(withPanelBase(`/panel/entegrasyonlar/${item.integration_id}/siparisler`, user, location.pathname), {
        state: { fromNotification: true, refreshAt: Date.now() },
      });
      return;
    }
    navigate(withPanelBase("/panel/entegrasyonlar", user, location.pathname));
  };

  const markAllNotificationsRead = () => {
    api.post("/support/notifications/mark-read").catch(() => {});
    const dismissed = getDismissedSystemIds();
    for (const n of systemNotifications) {
      if (n?.id_key) dismissed.add(n.id_key);
    }
    localStorage.setItem(storageKeys.dismissedSystem, JSON.stringify(Array.from(dismissed)));
    rememberDismissedOrderKeys(notifications.map((n) => n.id_key || notificationKey(n)));
    setSystemNotifications([]);
    setSupportNotifications([]);
    setNotifications([]);
    setNewOrderCount(0);
    localStorage.setItem(storageKeys.orderNotifications, "[]");
    localStorage.setItem(storageKeys.orderCount, "0");
    window.dispatchEvent(new CustomEvent("obihub:new-orders", { detail: { count: 0, notifications: [] } }));
  };

  return (
    <header className="relative z-[120] h-14 glass border-b border-border/70 flex items-center justify-between px-3 sm:px-4 md:px-6" data-testid="header">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground hover:bg-muted transition-colors"
          data-testid="menu-btn"
        >
          <Menu className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => navigate(withPanelBase("/panel", user, location.pathname))}
          className="md:hidden inline-flex items-center"
          data-testid="mobile-logo-home-btn"
          title="Dashboard"
        >
          <img src="/logomain.svg" alt="obihub" className="h-7 w-auto" />
        </button>

        <div className="hidden md:block">
          <h1 className="text-sm font-semibold text-foreground leading-tight" data-testid="tenant-title">{tenantName}</h1>
          <p className="text-[11px] text-muted-foreground font-medium">{breadcrumb}</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {supportModuleEnabled ? (
        <div className="relative" ref={supportRef}>
          <button
            type="button"
            onClick={() => {
              setSupportOpen((v) => {
                const next = !v;
                if (next) {
                  setSupportStep(1);
                  setSupportTopic("");
                  setSupportMessage("");
                }
                return next;
              });
            }}
            className="h-9 rounded-lg border border-[#1487a3]/40 bg-[#1487a3]/[0.08] px-3 text-[#1487a3] hover:bg-[#1487a3]/[0.14] transition-colors inline-flex items-center gap-1.5 font-semibold text-sm shadow-sm"
            data-testid="support-btn"
          >
            <LifeBuoy className="h-4 w-4" />
            Destek
          </button>

          {supportOpen &&
            createPortal(
              <div ref={supportPanelRef} className="fixed right-6 bottom-6 w-[420px] max-w-[94vw] rounded-3xl border border-border bg-card shadow-[0_30px_70px_rgba(2,12,27,0.28)] z-[9999] overflow-hidden">
              <div className="relative bg-[linear-gradient(140deg,#0d6e87_0%,#1487a3_55%,#22c8e0_100%)] px-5 py-5 text-white">
                <button
                  type="button"
                  onClick={() => setSupportOpen(false)}
                  className="absolute right-4 top-4 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                  title="Kapat"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="text-xl font-semibold">Merhaba</div>
                <div className="text-sm text-sky-100">Size bugün nasıl yardımcı olabiliriz?</div>
                <div className="mt-2 text-xs text-white/70">Ticket sistemi aktiftir.</div>
              </div>
              <div className="p-5 space-y-3">
                {supportStep === 1 ? (
                  <>
                    <div className="text-xs font-medium text-muted-foreground">1) Konuyu seçin</div>
                    <div className="grid grid-cols-2 gap-2">
                      {SUPPORT_TOPICS.map((topic) => (
                        <button
                          key={topic.key}
                          type="button"
                          onClick={() => {
                            setSupportTopic(topic.key);
                            setSupportStep(2);
                          }}
                          className="h-10 rounded-lg border border-border bg-muted/50 px-3 text-left text-xs font-medium text-foreground/80 hover:border-[#1487a3]/40 hover:bg-[#1487a3]/[0.06] hover:text-[#1487a3]"
                        >
                          {topic.label}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(withPanelBase("/panel/hesabim/destek-taleplerim", user, location.pathname))}
                      className="w-full h-10 rounded-xl border border-border bg-card text-foreground/80 font-semibold hover:bg-muted/50"
                    >
                      Destek Taleplerime Git
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-muted-foreground">
                        2) Sorunu yazın ({SUPPORT_TOPICS.find((x) => x.key === supportTopic)?.label || "Genel"})
                      </div>
                      <button
                        type="button"
                        onClick={() => setSupportStep(1)}
                        className="text-xs text-muted-foreground hover:text-foreground/80"
                      >
                        Konuyu Değiştir
                      </button>
                    </div>
                    <textarea
                      value={supportMessage}
                      onChange={(e) => setSupportMessage(e.target.value)}
                      placeholder="Lütfen sorununuzu mümkün olduğunca detaylı yazın..."
                      rows={5}
                      className="w-full rounded-lg border border-border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1487a3]/30"
                    />
                    <button
                      type="button"
                      onClick={submitSupportMessage}
                      disabled={supportSaving || !supportTopic || !supportMessage.trim()}
                      className="w-full h-11 rounded-xl bg-[linear-gradient(135deg,#1487a3_0%,#22c8e0_100%)] text-white font-semibold hover:brightness-105 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                      <Send className="h-4 w-4" />
                      {supportSaving ? "Gönderiliyor..." : "Ticket Oluştur"}
                    </button>
                  </>
                )}
              </div>
              </div>,
              document.body
            )}
        </div>
        ) : null}

        {/* ⌘K trigger */}
        <button
          type="button"
          onClick={() => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }))}
          className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-xs"
          title="Ara (⌘K)"
          data-testid="command-palette-btn"
        >
          <Search className="h-3.5 w-3.5" />
          <span>Ara</span>
          <kbd className="ml-1 pointer-events-none inline-flex h-5 select-none items-center gap-0.5 rounded border border-border bg-muted px-1 font-mono text-[10px] font-medium opacity-60">
            ⌘K
          </kbd>
        </button>

        {/* Dark mode toggle */}
        <button
          type="button"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="relative h-9 w-9 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted transition-colors inline-flex items-center justify-center"
          title={theme === "dark" ? "Açık mod" : "Koyu mod"}
          data-testid="theme-toggle-btn"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
        </button>

        <div className="relative" ref={notifRef}>
          <button
            type="button"
            onClick={openNotifications}
            className="relative h-9 w-9 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted transition-colors inline-flex items-center justify-center"
            data-testid="notifications-btn"
          >
            <Bell className="relative z-[1] h-4 w-4" strokeWidth={1.75} />
            {allNotifications.length > 0 && (
              <span className="absolute -right-1 -top-1 z-10 min-w-[18px] h-[18px] rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white inline-flex items-center justify-center">
                {allNotifications.length > 99 ? "99+" : allNotifications.length}
              </span>
            )}
            {allNotifications.length > 0 && (
              <span className="pointer-events-none absolute -right-1 -top-1 z-0 h-[18px] w-[18px] rounded-full bg-red-400/70 animate-ping" />
            )}
          </button>
        </div>

        {/* Account dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setAccountOpen(!accountOpen)}
            className="cursor-pointer h-9 w-9 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted transition-colors inline-flex items-center justify-center"
            data-testid="user-menu-btn"
            title="Ayarlar"
          >
            <Settings className="h-4 w-4" strokeWidth={1.75} />
          </button>
          {accountOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-lg border border-border bg-card shadow-lg p-1.5 text-sm z-[220] animate-in">
              <div className="px-3 py-2 border-b border-border mb-1">
                <div className="font-medium text-foreground text-xs">{user?.full_name}</div>
                <div className="text-[11px] text-muted-foreground">{userEmail}</div>
              </div>
              <button
                type="button"
                onClick={() => { setAccountOpen(false); navigate(withPanelBase("/panel/ayarlar", user, location.pathname)); }}
                className="w-full text-left rounded-md px-3 py-2 text-xs hover:bg-muted flex items-center gap-2 transition-colors"
                data-testid="settings-menu-item"
              >
                <Settings className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                Hesap Ayarları
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full text-left rounded-md px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                data-testid="logout-menu-item"
              >
                <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} />
                {"\u00c7\u0131k\u0131\u015f Yap"}
              </button>
            </div>
          )}
        </div>
      </div>
      {notifOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9996] bg-slate-900/30" onClick={() => setNotifOpen(false)} />
            <aside
              ref={notifPanelRef}
              className="fixed right-0 top-0 z-[9997] h-full w-[420px] max-w-[96vw] border-l border-border bg-card shadow-[0_22px_60px_rgba(2,12,27,0.25)]"
              data-testid="notifications-panel"
            >
              <div className="flex h-full flex-col">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">Bildirim Paneli</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNotifOpen(false)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted"
                    title="Kapat"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {allNotifications.length === 0 ? (
                    <div className="px-4 py-6 text-xs text-muted-foreground">Yeni bildirim yok.</div>
                  ) : (
                    allNotifications.map((n, idx) => {
                      if (n?.kind === "system") {
                        return (
                          <button
                            key={n.id_key || `sys-${idx}`}
                            type="button"
                            onClick={() => openOrderNotification(n)}
                            className="w-full border-b border-border px-4 py-3 text-left hover:bg-muted transition-colors"
                          >
                            <div className="text-xs font-semibold text-[#1487a3]">{n.title}</div>
                            <div className="mt-1 text-[12px] text-muted-foreground">{n.message}</div>
                          </button>
                        );
                      }
                      if (n?.kind === "support") {
                        return (
                          <button
                            key={n.id_key || `support-${idx}`}
                            type="button"
                            onClick={() => openOrderNotification(n)}
                            className="w-full border-b border-border px-4 py-3 text-left hover:bg-[#1487a3]/[0.05] transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="inline-flex rounded-full border border-[#1487a3]/30 bg-[#1487a3]/[0.08] px-2 py-0.5 text-[10px] font-semibold text-[#1487a3]">
                                Destek
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                {n.status === "closed"
                                  ? "Kapandı"
                                  : n.status === "resolved"
                                  ? "Çözüldü"
                                  : n.status === "queued"
                                  ? "Sırada"
                                  : n.status === "waiting_customer"
                                  ? "Müşteri Yanıtı"
                                  : n.status === "in_progress"
                                  ? "Devam Ediyor"
                                  : "Açık"}
                              </span>
                            </div>
                            <div className="mt-1 text-sm font-semibold text-foreground truncate">{n.title || "Destek Talebi"}</div>
                            <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{n.message || "Destek ekibinden yeni yanıt geldi."}</div>
                          </button>
                        );
                      }
                      const isProcessing = n.status === "processing";
                      const channelLabel = resolveChannelLabel(n);
                      return (
                        <button
                          key={n.id_key || `${n.channel_order_id}-${idx}`}
                          type="button"
                          onClick={() => openOrderNotification(n)}
                          className="w-full border-b border-border px-4 py-4 text-left hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="inline-flex items-center gap-2">
                              <span
                                className="inline-flex rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-semibold text-foreground/80"
                              >
                                {channelLabel}
                              </span>
                              <span
                              className={
                                isProcessing
                                  ? "inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
                                  : "inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700"
                              }
                            >
                              {isProcessing ? "Hazırlanıyor" : "Ödeme Bekleniyor"}
                              </span>
                            </div>
                            <span className="text-[11px] font-semibold text-foreground/80">{formatAmount(n.total_amount, n.currency)}</span>
                          </div>
                          <div className="mt-2 truncate text-sm font-semibold text-foreground">
                            {n.customer_name || "Müşteri"}
                          </div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            Sipariş: {n.channel_order_id || "-"}
                          </div>
                          <div className="mt-1 text-[11px] text-muted-foreground">
                            {isProcessing ? "Ödemesi alınmış sipariş, işlem/kargo bekliyor." : "Ödeme onayı bekleyen sipariş."}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="border-t border-border px-4 py-3">
                  <button
                    type="button"
                    onClick={markAllNotificationsRead}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                  >
                    Hepsini Okundu Olarak İşaretle
                  </button>
                </div>
              </div>
            </aside>
          </>,
          document.body
        )}
    </header>
  );
}
